import { useTranslation } from 'react-i18next'
import { Button, Content, Header, Alert } from '@/components'
import { clearAllSessionData, getToken } from '@/services/session'
import { resetProcess } from '@/services/navigation'
import { useConfig } from '@/services/config'
import { useEffect } from 'preact/hooks'
import { useNavigationGuard } from '@/services/navigation-guard'

/**
 * Session Expired Page Component
 *
 * Displayed when user session expires.
 * Informs user about session termination and provides option to start new session.
 */
export function SessionExpiredPage() {
  const { t } = useTranslation()
  const { navigateTo } = useNavigationGuard()
  const token = getToken()

  const { setExpirationTime } = useConfig()

  // Ensure session data is cleared when this page loads
  useEffect(() => {
    clearAllSessionData()
    resetProcess()
    setExpirationTime(null) // Clear expiration time to prevent timer from running
  }, [setExpirationTime])

  const handleStartNewSession = () => {
    // Clear any remaining data and reset process state
    clearAllSessionData()
    resetProcess()
    setExpirationTime(null) // Clear expiration time
    navigateTo('/')
  }

  return (
    <div className='bg-cencert-bg-gray min-h-screen'>
      <Header />

      <Content showSidebar={false}>
        <div className='max-w-2xl mx-auto py-8'>
          <h1 className='text-heading-3 sm:text-heading-2 text-gray-900 mb-8 text-center'>
            {t('sessionExpired.title')}
          </h1>

          <Alert
            variant='warning'
            message={t('sessionExpired.message')}
            className='mb-8'
          />

          <div className='bg-white rounded-lg shadow-sm p-6 mb-8'>
            <h2 className='text-heading-4 text-gray-900 mb-4'>
              {t('sessionExpired.nextSteps.title')}
            </h2>
            {token ? (
              <p className='text-gray-700 mb-6'>
                {t('sessionExpired.nextSteps.noTokenDescription')}
              </p>
            ) : (
              <>
                <p className='text-gray-700 mb-6'>
                  {t('sessionExpired.nextSteps.tokenDescription')}
                </p>
                <ul className='space-y-2 text-gray-700'>
                  <li className='flex items-start'>
                    <span className='text-cencert-blue mr-3 font-bold'>1.</span>
                    {t('sessionExpired.nextSteps.step1')}
                  </li>
                  <li className='flex items-start'>
                    <span className='text-cencert-blue mr-3 font-bold'>2.</span>
                    {t('sessionExpired.nextSteps.step2')}
                  </li>
                  <li className='flex items-start'>
                    <span className='text-cencert-blue mr-3 font-bold'>3.</span>
                    {t('sessionExpired.nextSteps.step3')}
                  </li>
                </ul>
              </>
            )}
          </div>

          {!token && (
            <div className='text-center'>
              <Button
                type='primary'
                size='lg'
                className='rounded-3xl tracking-[0.4px] px-8'
                onClick={handleStartNewSession}
              >
                {t('sessionExpired.startNewSession')}
              </Button>
            </div>
          )}

          <div className='text-center mt-4'>
            <p className='text-sm text-gray-500'>
              {t('sessionExpired.footer')}
            </p>
          </div>
        </div>
      </Content>
    </div>
  )
}
